/**
 * Catalog of every custom GA4 event this app fires. Keeping names here (and
 * only here) means: no typos scattered across ~40 pages, one place to see
 * everything tracked, and IDE-assisted autocomplete when adding a new call
 * site. Names follow GA4's snake_case convention; a few match GA4's
 * "recommended events" (login, sign_up, search) so GA4's default reports
 * pick them up automatically.
 */
export const ANALYTICS_EVENTS = {
  // Auth — fired from the redux-saga layer, not components (see
  // src/store/auth/login/saga.js and register/saga.js).
  AUTH: {
    LOGIN: "login",
    GUEST_LOGIN: "guest_login",
    SIGN_UP: "sign_up",
    LOGOUT: "logout",
  },

  // Feature usage — the core "are people using the product" signal. One
  // named event per distinct business action (not a generic "search" event
  // with a surface param) so each shows up self-explanatory in GA4's UI for
  // non-technical stakeholders, and so per-feature funnels/audiences can be
  // built directly on event name without needing a parameter filter.
  FEATURE: {
    // AI CMO (app scanner + Okara chat) — src/pages/AppScanner/AppScanner.jsx
    // ("AI CMO" and "App Scanner" are the same screens, route /ai-cmo).
    APP_SCAN_RUN: "app_scan_run",
    AI_CMO_CHAT_MESSAGE_SENT: "ai_cmo_chat_message_sent",
    // Any "Ask AI" assistant surface — floating widget (AskAIButton/Chat.jsx)
    // and the standalone /chatbot page (ChatBot.jsx). Same event, both use it.
    ASK_AI_OPENED: "ask_ai_opened",
    ASSISTANT_CHAT_MESSAGE_SENT: "assistant_chat_message_sent",
    // The 4 AI-agent cards (Articles/LinkedIn/X/Marketing Strategy) and the
    // full Marketing Strategy report — generate/copy/share/download, all
    // previously untracked despite being AI CMO's flagship output.
    AI_CMO_ARTICLE_GENERATED: "ai_cmo_article_generated",
    AI_CMO_ARTICLE_COPIED: "ai_cmo_article_copied",
    AI_CMO_LINKEDIN_POST_GENERATED: "ai_cmo_linkedin_post_generated",
    AI_CMO_LINKEDIN_POST_COPIED: "ai_cmo_linkedin_post_copied",
    AI_CMO_LINKEDIN_POST_SHARED: "ai_cmo_linkedin_post_shared",
    AI_CMO_X_POST_GENERATED: "ai_cmo_x_post_generated",
    AI_CMO_X_POST_COPIED: "ai_cmo_x_post_copied",
    AI_CMO_X_POST_SHARED: "ai_cmo_x_post_shared",
    AI_CMO_MARKETING_STRATEGY_GENERATED: "ai_cmo_marketing_strategy_generated",
    AI_CMO_MARKETING_STRATEGY_COPIED: "ai_cmo_marketing_strategy_copied",
    AI_CMO_MARKETING_STRATEGY_DOWNLOADED: "ai_cmo_marketing_strategy_downloaded",

    // Store Validator — src/pages/StoreValidator/StoreValidator.jsx
    STORE_VALIDATOR_RUN: "store_validator_run",
    STORE_VALIDATOR_SUBMITTED: "store_validator_submitted",
    STORE_VALIDATOR_PREVIEW_OPENED: "store_validator_preview_opened",
    STORE_VALIDATOR_SECTION_SWITCHED: "store_validator_section_switched",
    STORE_VALIDATOR_SECTION_REGENERATED: "store_validator_section_regenerated",
    STORE_VALIDATOR_FIELD_REVALIDATED: "store_validator_field_revalidated",
    STORE_VALIDATOR_PLATFORM_SELECTED: "store_validator_platform_selected",
    STORE_VALIDATOR_APP_CREATED: "store_validator_app_created",
    STORE_VALIDATOR_APP_OPENED: "store_validator_app_opened",
    STORE_VALIDATOR_APP_DELETED: "store_validator_app_deleted",

    // Keyword Suggestions — src/pages/KeywordSuggestions/KeywordSuggestions.jsx
    KEYWORD_SUGGESTIONS_APP_SELECTED: "keyword_suggestions_app_selected",
    KEYWORD_SUGGESTIONS_TRACKING_ADDED: "keyword_suggestions_tracking_added",
    KEYWORD_SUGGESTIONS_KEYWORD_TOGGLED: "keyword_suggestions_keyword_toggled",
    KEYWORD_SUGGESTIONS_SELECT_ALL_TOGGLED: "keyword_suggestions_select_all_toggled",
    KEYWORD_SUGGESTIONS_COMPARE_ADDED: "keyword_suggestions_compare_added",
    KEYWORD_SUGGESTIONS_COMPARE_OPENED: "keyword_suggestions_compare_opened",
    KEYWORD_SUGGESTIONS_COMPARE_REMOVED: "keyword_suggestions_compare_removed",

    // Explorers (search-as-you-type analysis tools, no persistent tracking list)
    KEYWORD_SEARCH: "keyword_search", // Keyword Explorer, /keyword-analysis
    CATEGORY_SEARCH: "category_search", // Genre Explorer, /category-analysis

    // Keyword Explorer, beyond the search itself.
    KEYWORD_EXPLORER_PLATFORM_CHANGED: "keyword_explorer_platform_changed",
    KEYWORD_EXPLORER_FILTER_APPLIED: "keyword_explorer_filter_applied",
    KEYWORD_EXPLORER_FILTER_REMOVED: "keyword_explorer_filter_removed",
    KEYWORD_EXPLORER_PAGINATED: "keyword_explorer_paginated",
    KEYWORD_EXPLORER_SEARCH_CLEARED: "keyword_explorer_search_cleared",
    KEYWORD_EXPLORER_VIEW_TYPE_CHANGED: "keyword_explorer_view_type_changed",
    // Clicked a result app (row/card/CTA); fires even if the detail page then
    // fails to load, unlike APP_VIEWED. Params: app_id, app_name, keyword, platform.
    KEYWORD_EXPLORER_APP_SELECTED: "keyword_explorer_app_selected",

    // Genre Explorer, beyond the search itself.
    CATEGORY_EXPLORER_PLATFORM_CHANGED: "category_explorer_platform_changed",
    CATEGORY_EXPLORER_FILTER_APPLIED: "category_explorer_filter_applied",
    CATEGORY_EXPLORER_FILTER_REMOVED: "category_explorer_filter_removed",
    CATEGORY_SEARCH_CLEARED: "category_search_cleared",
    // Clicked a result app row. Params: app_id, app_name, genre, platform.
    CATEGORY_EXPLORER_APP_SELECTED: "category_explorer_app_selected",

    // Publisher Explorer, beyond PUBLISHER_SEARCH/PUBLISHER_DASHBOARD_VIEWED below.
    PUBLISHER_EXPLORER_PLATFORM_CHANGED: "publisher_explorer_platform_changed",
    PUBLISHER_EXPLORER_FILTER_APPLIED: "publisher_explorer_filter_applied",
    PUBLISHER_EXPLORER_FILTER_REMOVED: "publisher_explorer_filter_removed",
    PUBLISHER_SEARCH_CLEARED: "publisher_search_cleared",
    // The search page has a "publisher" vs "keyword" search-mode toggle;
    // switching modes, and the keyword-mode search itself, were both
    // previously untracked (only publisher-mode search fired PUBLISHER_SEARCH).
    PUBLISHER_EXPLORER_SEARCH_MODE_CHANGED: "publisher_explorer_search_mode_changed",
    PUBLISHER_KEYWORD_SEARCH: "publisher_keyword_search",
    PUBLISHER_EXPLORER_SORT_CHANGED: "publisher_explorer_sort_changed", // results-table column sort

    // Tracking pages (search + persist an app/keyword/genre to a watched list)
    GENRE_TRACKING_SEARCH: "genre_tracking_search", // /category-tracking ("Genre Tracking")
    GENRE_TRACKING_APP_ADDED: "genre_tracking_app_added",
    KEYWORD_TRACKING_SEARCH: "keyword_tracking_search", // /keyword-tracking
    KEYWORD_TRACKING_APP_ADDED: "keyword_tracking_app_added",
    APP_TRACKING_SEARCH: "app_tracking_search", // /app-tracking
    APP_TRACKING_APP_ADDED: "app_tracking_app_added",
    // App Tracking dashboard management — untracking, custom groups, platform
    // filter, and the comparison view's tabs/export/date-range. The "Track
    // More" modal's own single/bulk add and add/remove-from-group actions
    // reuse APP_TRACKING_APP_ADDED / APP_TRACKING_APP_ADDED_TO_GROUP /
    // APP_TRACKING_APP_REMOVED_FROM_GROUP below rather than new events, since
    // they're the same business action as the main search-flow add.
    APP_TRACKING_APP_REMOVED: "app_tracking_app_removed",
    APP_TRACKING_GROUP_CREATED: "app_tracking_group_created",
    APP_TRACKING_GROUP_DELETED: "app_tracking_group_deleted",
    APP_TRACKING_GROUP_SWITCHED: "app_tracking_group_switched",
    APP_TRACKING_APP_ADDED_TO_GROUP: "app_tracking_app_added_to_group",
    APP_TRACKING_APP_REMOVED_FROM_GROUP: "app_tracking_app_removed_from_group",
    APP_TRACKING_PLATFORM_FILTER_CHANGED: "app_tracking_platform_filter_changed",
    APP_TRACKING_COMPARISON_TAB_CHANGED: "app_tracking_comparison_tab_changed",
    APP_TRACKING_COMPARISON_EXPORTED: "app_tracking_comparison_exported",
    APP_TRACKING_DATE_RANGE_COMPARED: "app_tracking_date_range_compared",
    APP_TRACKING_SHORTCUT_CLICKED: "app_tracking_shortcut_clicked", // header quick-shortcut buttons

    // Publisher Explorer — search page (MarketResearch/PublisherAnalysis.jsx)
    // and the resulting per-publisher dashboard (PublisherAnalysisContainer.jsx)
    PUBLISHER_SEARCH: "publisher_search",
    PUBLISHER_DASHBOARD_VIEWED: "publisher_dashboard_viewed",
    // Fired instead of PUBLISHER_DASHBOARD_VIEWED whenever the dashboard is
    // reached some other way than the search page's own CTA button (e.g. a
    // publisher-name link inline in a results table).
    PUBLISHER_NAME_CLICKED: "publisher_name_clicked",
    // Per-publisher dashboard (route /publisher-apps/:itemId) — was entirely
    // untracked; these cover its platform tab, CSV/XLSX/PDF export, and app
    // clicks within that dashboard's own tables.
    PUBLISHER_DASHBOARD_TAB_CHANGED: "publisher_dashboard_tab_changed",
    PUBLISHER_DASHBOARD_EXPORTED: "publisher_dashboard_exported",
    PUBLISHER_DASHBOARD_APP_SELECTED: "publisher_dashboard_app_selected",

    // Social Media Comparison — src/pages/SocialMediaComparison
    SOCIAL_COMPARISON_APP_SELECTED: "social_comparison_app_selected",
    SOCIAL_COMPARISON_SECTION_VIEWED: "social_comparison_section_viewed", // SEO/Marketing/Ads/Platforms accordion switch
    // Any outbound click off this page to a real external profile/ad/listing;
    // param `link_type` distinguishes which (e.g. "profile", "ad", "competitor_listing").
    SOCIAL_COMPARISON_OUTBOUND_CLICKED: "social_comparison_outbound_clicked",

    // UGC Explorer — src/pages/UGCExplorer
    UGC_EXPLORER_APP_SELECTED: "ugc_explorer_app_selected",
    UGC_EXPLORER_PLATFORM_CHANGED: "ugc_explorer_platform_changed",
    UGC_EXPLORER_FILTER_APPLIED: "ugc_explorer_filter_applied",
    UGC_EXPLORER_FILTER_CLEARED: "ugc_explorer_filter_cleared",
    UGC_EXPLORER_SORT_CHANGED: "ugc_explorer_sort_changed",
    UGC_EXPLORER_REFRESHED: "ugc_explorer_refreshed", // manual "force refresh" of an app's UGC coverage
    UGC_EXPLORER_PAGINATED: "ugc_explorer_paginated",
    UGC_VIDEO_OPENED: "ugc_video_opened", // opened a content card's detail view
    // Outbound click off a video/post to its real platform (watch link or
    // channel/account link); param `link_type` distinguishes which.
    UGC_OUTBOUND_CLICKED: "ugc_outbound_clicked",
    // Comment sentiment (SENTIMENT_UGC_API.md) - "Generate" claims the app
    // against the user's sentiment app limit.
    UGC_SENTIMENT_GENERATED: "ugc_sentiment_generated",
    UGC_SENTIMENT_VIEWED: "ugc_sentiment_viewed",

    // Influencer Explorer — src/pages/InfluencerExplorer
    INFLUENCER_EXPLORER_FILTERED: "influencer_explorer_filtered",
    INFLUENCER_PROFILE_VIEWED: "influencer_profile_viewed",
    INFLUENCER_EXPLORER_FILTER_CLEARED: "influencer_explorer_filter_cleared",
    INFLUENCER_MODE_CHANGED: "influencer_mode_changed", // "All Influencers" vs "My Favourites"
    INFLUENCER_EXPLORER_PAGINATED: "influencer_explorer_paginated",
    INFLUENCER_FAVOURITE_TOGGLED: "influencer_favourite_toggled", // bookmark/unbookmark
    // Outbound click to the influencer's real profile, or to one of their
    // individual posts; param `link_type` is "profile" or "post".
    INFLUENCER_OUTBOUND_CLICKED: "influencer_outbound_clicked",
    INFLUENCER_APP_COVERAGE_EXPANDED: "influencer_app_coverage_expanded", // "See all posts about this app"

    // Ad Discovery / Creative Insights — src/pages/Insights/CreativeInsightsDashboard.jsx
    AD_DISCOVERY_SEARCH: "ad_discovery_search",
    AD_DISCOVERY_FILTER_APPLIED: "ad_discovery_filter_applied",
    AD_DISCOVERY_FILTER_REMOVED: "ad_discovery_filter_removed",
    AD_DISCOVERY_PAGINATED: "ad_discovery_paginated",
    // Outbound click on a specific ad card; param `link_type` is "view_ad"
    // (Meta Ads Library) or "landing_page" (the advertiser's own link_url).
    AD_DISCOVERY_AD_OPENED: "ad_discovery_ad_opened",

    // Weekly Insights (MetaInsight/*) and Custom Insights (CustomInsight/*) —
    // both render several data sections at once rather than clickable tabs,
    // so these fire per-section on data load, param insight_type identifies
    // which (see WEEKLY_INSIGHT_TYPES / CUSTOM_INSIGHT_TYPES below).
    WEEKLY_INSIGHT_VIEWED: "weekly_insight_viewed",
    WEEKLY_INSIGHT_APP_SELECTED: "weekly_insight_app_selected",
    // Beyond the per-section VIEWED/APP_SELECTED above: platform switch is
    // dashboard-wide; the rest (locale, filters, pagination, "View All") are
    // per-section, so pass insight_type on every one of these.
    WEEKLY_INSIGHT_PLATFORM_CHANGED: "weekly_insight_platform_changed",
    WEEKLY_INSIGHT_LOCALE_CHANGED: "weekly_insight_locale_changed",
    WEEKLY_INSIGHT_FILTER_APPLIED: "weekly_insight_filter_applied",
    WEEKLY_INSIGHT_FILTER_REMOVED: "weekly_insight_filter_removed",
    WEEKLY_INSIGHT_PAGINATED: "weekly_insight_paginated",
    WEEKLY_INSIGHT_VIEW_ALL_CLICKED: "weekly_insight_view_all_clicked",

    CUSTOM_INSIGHT_VIEWED: "custom_insight_viewed",
    CUSTOM_INSIGHT_APP_SELECTED: "custom_insight_app_selected",
    // Beyond the above: platform/sort/expand/pagination are per-page (pass
    // insight_type); the last two are TopGenres2025-specific (genre-only,
    // no app entities on that page).
    CUSTOM_INSIGHT_PLATFORM_CHANGED: "custom_insight_platform_changed",
    CUSTOM_INSIGHT_SORT_CHANGED: "custom_insight_sort_changed",
    CUSTOM_INSIGHT_LIST_EXPANDED: "custom_insight_list_expanded", // "See More"/"See Less"
    CUSTOM_INSIGHT_PAGINATED: "custom_insight_paginated",
    CUSTOM_INSIGHT_DATE_RANGE_APPLIED: "custom_insight_date_range_applied",
    CUSTOM_INSIGHT_GENRE_EXPANDED: "custom_insight_genre_expanded", // TopGenres2025 row expand/collapse
    CUSTOM_INSIGHT_GENRE_COMPARED: "custom_insight_genre_compared", // TopGenres2025 "Compare" action

    // Home dashboard — src/pages/Dashboard/OptimizedDashboard.jsx. Had zero
    // tracking despite being the main post-login landing page; param
    // `section` distinguishes the "Most Tracked Games" vs "Top Performer
    // Apps" widgets (also reused for the "Explore More Tools" grid, where
    // `section` isn't applicable and only `tool_name` is passed).
    DASHBOARD_PLATFORM_TOGGLED: "dashboard_platform_toggled",
    DASHBOARD_FILTER_APPLIED: "dashboard_filter_applied",
    DASHBOARD_FILTER_REMOVED: "dashboard_filter_removed",
    DASHBOARD_PAGINATED: "dashboard_paginated",
    DASHBOARD_APP_SELECTED: "dashboard_app_selected",
    DASHBOARD_TOOL_SELECTED: "dashboard_tool_selected", // an "Explore More Tools" card click

    // Keyword Tracking dashboard — src/pages/KeywordTracking/keywordDetails.jsx
    // (route /xr/keyword, reached after KEYWORD_TRACKING_APP_ADDED). The
    // landing page's search+add is tracked; this dashboard, where users
    // actually view rank history and manage keywords, previously had none.
    KEYWORD_TRACKING_LOCALE_CHANGED: "keyword_tracking_locale_changed",
    KEYWORD_TRACKING_KEYWORD_VIEWED: "keyword_tracking_keyword_viewed", // expand row / switch to a tracked keyword
    KEYWORD_TRACKING_KEYWORDS_ADDED: "keyword_tracking_keywords_added", // added from within the dashboard itself
    KEYWORD_TRACKING_KEYWORD_REMOVED: "keyword_tracking_keyword_removed",
    KEYWORD_TRACKING_EXPORTED: "keyword_tracking_exported",
    KEYWORD_TRACKING_LIVE_RANKING_VIEWED: "keyword_tracking_live_ranking_viewed",

    // Genre Tracking dashboard — src/pages/CategoryTracking/CategoryDetails.jsx
    // (route /xr/category). Mirrors KEYWORD_TRACKING_* above; same gap, same fix.
    GENRE_TRACKING_LOCALE_CHANGED: "genre_tracking_locale_changed",
    GENRE_TRACKING_GENRE_VIEWED: "genre_tracking_genre_viewed",
    GENRE_TRACKING_GENRES_ADDED: "genre_tracking_genres_added",
    GENRE_TRACKING_GENRE_REMOVED: "genre_tracking_genre_removed",
    GENRE_TRACKING_EXPORTED: "genre_tracking_exported",
    GENRE_TRACKING_LIVE_RANKING_VIEWED: "genre_tracking_live_ranking_viewed",

    // Universal "opened a specific app's detail page" — fires once from
    // gameDashboard.jsx (route /xr/*) regardless of which surface above the
    // click came from; pair with that surface's own *_SELECTED/_ADDED event
    // (fired earlier, on the list/search page) to attribute the journey.
    // Also carries app_name plus, when the navigating surface set them in
    // route state, source_surface (which page/list sent the user here) and
    // search_keyword (the keyword/term that was searched, if any) — set
    // these in navigate(..., { state }) at each call site so the full
    // search -> click -> view funnel can be joined in GA4.
    APP_VIEWED: "app_viewed",
  },

  // Enumerated values for FEATURE.WEEKLY_INSIGHT_VIEWED's insight_type param.
  WEEKLY_INSIGHT_TYPES: {
    TRENDING: "trending",
    NEW_RELEASE: "new_release",
    TOP_SELLING: "top_selling",
    TOP_EARNING: "top_earning",
    POPULAR: "popular",
    PRE_ORDERS: "pre_orders",
    TOP_DISCOUNTS: "top_discounts",
  },

  // Enumerated values for FEATURE.CUSTOM_INSIGHT_VIEWED's insight_type param.
  CUSTOM_INSIGHT_TYPES: {
    TOP_GENRES: "top_genres",
    TOP_REVENUE: "top_revenue",
    TOP_DOWNLOADS: "top_downloads",
    TOP_AVERAGE_RATING: "top_average_rating",
    TOP_APP_SCORES: "top_app_scores",
    TOP_KEYWORD_CATEGORIES: "top_keyword_categories",
    TOP_REVIEWED: "top_reviewed",
  },

  // Conversion / lead-gen — the events product & marketing care most about.
  CONVERSION: {
    CONTACT_FORM_SUBMITTED: "contact_form_submitted",
    SIDEQUEST_PREREGISTERED: "sidequest_preregistered",
    PLAN_UPGRADE_STARTED: "plan_upgrade_started",
    PLAN_UPGRADE_COMPLETED: "plan_upgrade_completed",
  },

  // Blog — src/pages/Blog/{BlogList,BlogDetail}.jsx. Marketing content
  // engagement: which posts/categories draw clicks and reads.
  CONTENT: {
    BLOG_CATEGORY_FILTERED: "blog_category_filtered", // list page, category pill clicked
    BLOG_POST_SELECTED: "blog_post_selected", // clicked into a post from a card/link
    BLOG_POST_VIEWED: "blog_post_viewed", // landed on the post detail page
  },
};

/**
 * GA4 event params should stay flat and small (GA4 caps at 25 params/event,
 * 100 chars per string value). This helper just enforces "no undefined/null
 * keys" so adapters don't have to.
 * @param {Record<string, unknown>} params
 */
export const cleanEventParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null)
  );
